import { computed, inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { MessagingService } from '../../components/messaging/messaging.service';
import { BankingDetails } from '../../model/banking-details.model';
import { RepairerSiteHistory, User, UserRepairerSite } from '../../model/user.model';
import { RepairerAdminService } from '../../services/repairer-admin.service';
import { ToolbarService } from '../../services/toolbar.service';

export interface LocationAdminState {
  userRepairerSite: UserRepairerSite;
  usersOfRepairerSite: User[];
  bankingDetails: BankingDetails | null;
  repairerSiteHistories: RepairerSiteHistory[] | [];
  isLoading: boolean;
}

export const initialState: LocationAdminState = {
  userRepairerSite: null,
  usersOfRepairerSite: [],
  bankingDetails: null,
  repairerSiteHistories: [],
  isLoading: false
};

@Injectable({ providedIn: 'root' })
export class LocationAdminStore extends ComponentStore<LocationAdminState> {
  userRepairerSite = this.selectSignal((state) => state.userRepairerSite);
  // Add bankingDetails and usersOfRepairerSite for future use
  bankingDetails = this.selectSignal((state) => state.bankingDetails);
  usersOfRepairerSite = this.selectSignal((state) => state.usersOfRepairerSite);
  repairerSiteHistories = this.selectSignal((state) => state.repairerSiteHistories);
  isLoading = this.selectSignal((state) => state.isLoading);

  emailSubscriptionEvents = computed(() => this.userRepairerSite()?.emailSubscriptionEvents ?? []);
  emailSubscriptionRecipients = computed(() => this.userRepairerSite()?.emailSubscriptionRecipients ?? []);

  #message = inject(MessagingService);
  #toolbarService = inject(ToolbarService);
  #repairerAdminService = inject(RepairerAdminService);
  #repAdminService = inject(RepairerAdminService);

  constructor() {
    super(initialState);
  }

  getUsersOfRepairerSite = this.effect((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.#toolbarService.setCloudState('FETCHING');
        this.patchState({ isLoading: true });
      }),
      switchMap(() => {
        return this.#repAdminService.getUsersOfRepairerSite().pipe(
          tapResponse({
            next: (usersOfRepairerSite: User[]) => {
              this.patchState({
                usersOfRepairerSite,
                isLoading: false
              });
              this.#toolbarService.setCloudState('RESTING');
            },
            error: () => {
              this.#message.error('Could not retrieve the users of repairer site.');
              this.patchState({ isLoading: false });
              this.#toolbarService.setCloudState('RESTING');
            }
          })
        );
      })
    )
  );

  getCompanyInformationHistory = this.effect((trigger$: Observable<number>) => {
    return trigger$.pipe(
      tap(() => {
        this.patchState({ isLoading: true });
        this.#toolbarService.setCloudState('FETCHING');
      }),
      switchMap((id: number) => {
        return this.#repairerAdminService.getRepairerSiteHistory(id).pipe(
          tapResponse({
            next: (repairerSiteHistories: RepairerSiteHistory[]) => {
              this.patchState({ isLoading: false, repairerSiteHistories });
              this.#toolbarService.setCloudState('RESTING');
            },
            error: () => {
              this.#message.error('Could not get repairer site history');
              this.patchState({ isLoading: false, repairerSiteHistories: [] });
              this.#toolbarService.setCloudState('RESTING');
            }
          })
        );
      })
    );
  });

  setUserRepairerSite(userRepairerSite: UserRepairerSite): void {
    this.patchState({ userRepairerSite });
  }

  resetStoreState() {
    this.patchState({
      userRepairerSite: null,
      repairerSiteHistories: [],
      usersOfRepairerSite: []
    });
  }
}
